import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  TaskAlt as PaidIcon,
  WarningAmberRounded as OverdueIcon,
} from '@mui/icons-material';
import { Repeat as RepeatIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigationIntent } from '../../hooks/useNavigationIntent';
import { billApi, Bill, CreateBillData, PayBillData } from '../../services/billApi';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { useAuth } from '../../contexts/AuthContext';
import { canModifyTransaction } from '../../utils/contextPermissions';
import { canPayBills, isBillOverdue, parseLocalDate, sumByCurrency } from '../../utils/bills';
import { chart } from '../../theme/chart';
import { BillFormDialog } from './BillFormDialog';
import { PayBillDialog } from './PayBillDialog';
import { PlanningCard } from './PlanningCard';

const chipSx = { height: 20, fontSize: 11, '& .MuiChip-label': { px: 0.9 }, '& .MuiChip-icon': { fontSize: 13, ml: 0.5 } } as const;
const smallButtonSx = { textTransform: 'none', borderRadius: 2, fontSize: 12, fontWeight: 600, py: 0.25, px: 1.25, minWidth: 0 } as const;

/**
 * The "Contas Pendentes" section: every open bill of the current view,
 * soonest due first, with overdue ones flagged and settleable in place.
 * Amounts keep their cents here: these are the exact sums to be paid.
 */
export const UpcomingBillsCard: React.FC = () => {
  const { t, i18n } = useTranslation(['planning', 'transactions']);
  const { selectedContextId, selectedContext } = useFinancialContexts();
  const { state: authState } = useAuth();

  const [bills, setBills] = useState<Bill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  // "Registrar → Nova conta" lands here with the form already open.
  useNavigationIntent('open', 'bill', () => {
    setEditingBill(null);
    setFormOpen(true);
  });
  const [payingBill, setPayingBill] = useState<Bill | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; bill: Bill } | null>(null);
  const [paidMessage, setPaidMessage] = useState<string | null>(null);

  const contextRole = selectedContext?.memberRole;
  const currentUserId = authState.user?.id;
  const defaultCurrency =
    selectedContext?.defaultCurrency || authState.user?.defaultCurrency || 'BRL';

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setBills(await billApi.list({ contextId: selectedContextId, status: 'open' }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedContextId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatCurrency = useCallback(
    (amount: number, currency: string) =>
      new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(amount),
    [i18n.language],
  );

  const formatDueDate = useCallback(
    (isoDate: string) => {
      const date = parseLocalDate(isoDate);
      const sameYear = date.getFullYear() === new Date().getFullYear();
      return date.toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        ...(sameYear ? {} : { year: 'numeric' }),
      });
    },
    [i18n.language],
  );

  const pendingTotals = useMemo(() => sumByCurrency(bills), [bills]);

  const createBill = async (data: CreateBillData) => {
    await billApi.create({ ...data, contextId: selectedContextId });
    await load();
  };

  const updateBill = async (data: CreateBillData) => {
    if (!editingBill) return;
    const { contextId: _ignored, ...changes } = data;
    await billApi.update(editingBill.id, changes);
    await load();
  };

  const payBill = async (data: PayBillData) => {
    if (!payingBill) return;
    const result = await billApi.pay(payingBill.id, data);
    // An installment or recurring bill leaves a successor behind; say so.
    setPaidMessage(
      result.nextBill
        ? t('planning:bills.paidWithNext', { date: formatDueDate(result.nextBill.dueDate) })
        : (t('planning:bills.paid') as string),
    );
    await load();
  };

  const deleteBill = async (bill: Bill) => {
    setMenuAnchor(null);
    if (!window.confirm(t('planning:bills.deleteConfirm', { description: bill.description }))) {
      return;
    }

    try {
      await billApi.remove(bill.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  return (
    <PlanningCard
      title={t('planning:bills.title')}
      aside={
        canPayBills(contextRole) ? (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => {
              setEditingBill(null);
              setFormOpen(true);
            }}
            aria-label={t('planning:bills.add') as string}
            sx={{ ...smallButtonSx, borderColor: 'rgba(0,0,0,0.18)' }}
          >
            + {t('planning:bills.add')}
          </Button>
        ) : undefined
      }
    >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : bills.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1.5,
              py: 4,
            }}
          >
            <PaidIcon sx={{ fontSize: 40, color: chart.ok, opacity: 0.6 }} />
            <Typography sx={{ fontSize: 13, color: chart.inkMuted, maxWidth: 280 }}>
              {t('planning:bills.empty')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {bills.map((bill, index) => {
              const overdue = isBillOverdue(bill);
              const canModify = canModifyTransaction({
                transactionUserId: bill.userId,
                currentUserId,
                contextRole,
              });

              return (
                <Box
                  key={bill.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1,
                    borderTop: index ? `1px solid ${chart.grid}` : 'none',
                    fontSize: 13,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, maxWidth: '100%' }}>
                      {bill.description}
                    </Typography>
                    {bill.installmentNumber && bill.installmentTotal && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('planning:bills.installment', {
                          number: bill.installmentNumber,
                          total: bill.installmentTotal,
                        })}
                        sx={{ ...chipSx, color: chart.inkSecondary }}
                      />
                    )}
                    {bill.recurrenceRule && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<RepeatIcon />}
                        label={t(`planning:bills.form.recurrenceOptions.${bill.recurrenceRule}`)}
                        sx={{ ...chipSx, color: chart.inkSecondary }}
                      />
                    )}
                    {overdue && (
                      // Icon + text, never colour alone, so the state
                      // survives any kind of colour vision.
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<OverdueIcon />}
                        label={t('planning:bills.overdue')}
                        sx={{ ...chipSx, color: chart.spend, borderColor: `${chart.spend}66`, background: `${chart.spend}12`, fontWeight: 700 }}
                      />
                    )}
                    <Box component="span" sx={{ fontSize: 12, color: overdue ? chart.spend : chart.inkMuted, fontWeight: overdue ? 600 : 400 }}>
                      {overdue
                        ? t('planning:bills.overdueSince', { date: formatDueDate(bill.dueDate) })
                        : t('planning:bills.dueOn', { date: formatDueDate(bill.dueDate) })}
                      {bill.merchantName ? ` · ${bill.merchantName}` : ''}
                    </Box>
                  </Box>

                  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {formatCurrency(Number(bill.amount), bill.currency)}
                  </Box>

                  {canPayBills(contextRole) && (
                    <Button size="small" variant="outlined" onClick={() => setPayingBill(bill)} sx={smallButtonSx}>
                      {t('planning:bills.pay')}
                    </Button>
                  )}

                  {canModify && (
                    <IconButton
                      size="small"
                      aria-label={t('planning:bills.rowMenu') as string}
                      onClick={(event) => setMenuAnchor({ element: event.currentTarget, bill })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 'auto', pt: 1, borderTop: `1px solid ${chart.grid}`, fontSize: 12, color: chart.inkMuted }}>
              <span>{t('planning:bills.totalPending')}</span>
              <Box component="span" sx={{ color: chart.ink, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {pendingTotals
                  .map(({ currency, total }) => formatCurrency(total, currency))
                  .join(' · ')}
              </Box>
            </Stack>
          </Box>
        )}

        <Menu
          anchorEl={menuAnchor?.element}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                setEditingBill(menuAnchor.bill);
                setFormOpen(true);
              }
              setMenuAnchor(null);
            }}
          >
            {t('planning:bills.menu.edit')}
          </MenuItem>
          <MenuItem onClick={() => menuAnchor && deleteBill(menuAnchor.bill)}>
            {t('planning:bills.menu.delete')}
          </MenuItem>
        </Menu>

        <BillFormDialog
          open={formOpen}
          bill={editingBill}
          defaultCurrency={defaultCurrency}
          onClose={() => {
            setFormOpen(false);
            setEditingBill(null);
          }}
          onSubmit={editingBill ? updateBill : createBill}
        />

        <PayBillDialog
          bill={payingBill}
          onClose={() => setPayingBill(null)}
          onConfirm={payBill}
        />

        <Snackbar
          open={Boolean(paidMessage)}
          autoHideDuration={5000}
          onClose={() => setPaidMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setPaidMessage(null)}>
            {paidMessage}
          </Alert>
        </Snackbar>
    </PlanningCard>
  );
};
